/**
 * Professional App Version Checker & Cache Buster
 * Detects new deployments, clears browser/service-worker caches, and reloads to the latest build.
 */

const LOCAL_VERSION_KEY = 'app_current_version';
const LOCAL_BUILD_TIME_KEY = 'app_current_build_time';
const CHECK_INTERVAL_MS = 5 * 60 * 1000; // Check every 5 minutes

let isUpdating = false;

/**
 * Completely clears browser application caches and service workers
 */
export async function clearAppCaches() {
    try {
        // 1. Clear CacheStorage (Service Worker caches)
        if ('caches' in window) {
            const cacheKeys = await caches.keys();
            await Promise.all(cacheKeys.map(key => caches.delete(key)));
            console.log('[VersionChecker] CacheStorage cleared:', cacheKeys);
        }

        // 2. Unregister Service Workers
        if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            for (const registration of registrations) {
                await registration.unregister();
                console.log('[VersionChecker] ServiceWorker unregistered:', registration);
            }
        }
    } catch (err) {
        console.warn('[VersionChecker] Error clearing caches:', err);
    }
}

/**
 * Performs a hard reload to ensure all new JS/CSS bundles are loaded
 */
export function reloadWithCacheBuster() {
    try {
        const url = new URL(window.location.href);
        url.searchParams.set('_v_reload', Date.now().toString());
        window.location.replace(url.toString());
    } catch (e) {
        window.location.reload(true);
    }
}

/**
 * Checks for a new app version from /version.json
 * @param {boolean} forceCheck - If true, checks immediately even if recently checked
 * @returns {Promise<boolean>} - True if new version was detected and update initiated
 */
export async function checkAppVersion(forceCheck = false) {
    if (isUpdating) return false;

    try {
        // Fetch version.json with anti-cache headers and timestamp query
        const response = await fetch(`/version.json?_t=${Date.now()}`, {
            method: 'GET',
            cache: 'no-store',
            headers: {
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                'Pragma': 'no-cache',
                'Expires': '0'
            }
        });

        if (!response.ok) {
            return false;
        }

        const remote = await response.json();
        if (!remote || !remote.version) {
            return false;
        }

        const currentVersion = localStorage.getItem(LOCAL_VERSION_KEY);
        const currentBuildTime = parseInt(localStorage.getItem(LOCAL_BUILD_TIME_KEY) || '0', 10);
        const remoteBuildTime = parseInt(remote.buildTime || '0', 10);

        // First visit or clean storage: save current remote metadata
        if (!currentVersion) {
            localStorage.setItem(LOCAL_VERSION_KEY, remote.version);
            localStorage.setItem(LOCAL_BUILD_TIME_KEY, String(remoteBuildTime || Date.now()));
            return false;
        }

        // Check if version changed or build timestamp is newer
        const hasNewVersion = remote.version !== currentVersion || (remoteBuildTime > 0 && remoteBuildTime > currentBuildTime);

        if (hasNewVersion) {
            console.log(`[VersionChecker] New version detected: ${remote.version} (current: ${currentVersion}). Clearing cache and reloading...`);
            isUpdating = true;

            // Trigger global event in case UI wants to show an update alert
            window.dispatchEvent(new CustomEvent('appVersionUpdate', { detail: remote }));

            // Update stored version
            localStorage.setItem(LOCAL_VERSION_KEY, remote.version);
            if (remoteBuildTime) {
                localStorage.setItem(LOCAL_BUILD_TIME_KEY, String(remoteBuildTime));
            }

            // Clear browser cache & reload after a brief moment
            await clearAppCaches();

            // Give any UI toast 800ms to display before reloading
            setTimeout(() => {
                reloadWithCacheBuster();
            }, 800);

            return true;
        }

        return false;
    } catch (error) {
        // Network or fetch error: fail silently in background
        return false;
    }
}

/**
 * Initializes the automated version checking lifecycle
 */
export function initVersionChecker() {
    // 1. Initial check on mount
    checkAppVersion();

    // 2. Check when tab becomes visible again
    const onVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
            checkAppVersion();
        }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    // 3. Periodic interval check (every 5 minutes)
    const intervalId = setInterval(() => {
        checkAppVersion();
    }, CHECK_INTERVAL_MS);

    return () => {
        document.removeEventListener('visibilitychange', onVisibilityChange);
        clearInterval(intervalId);
    };
}
